import React, { useState } from 'react';
import './LimitCalculator.css';
import { motion } from 'framer-motion';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const LimitCalculator = () => {
  const [inputData, setInputData] = useState({
    function: '',
    variable: 'x',
    point: '',
    direction: 'twoSided'
  });

  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInputData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const calculateLimit = () => {
    const { function: func, variable, point, direction } = inputData;

    // Validate inputs
    if (!func || !point) {
      setError('Please enter a function and a point');
      return;
    }

    try {
      // Parse the point
      const pointNum = parseFloat(point);
      if (isNaN(pointNum)) {
        throw new Error('Please enter a valid number for the point');
      }

      // Simple function parser and evaluator
      const safeEval = (funcStr, varName, value) => {
        // Create a safe evaluation context
        const mathContext = { ...Math };
        const context = { [varName]: value, ...mathContext };
        
        try {
          // Use Function constructor for evaluation
          return new Function(`with(this) { return ${funcStr} }`).call(context);
        } catch (err) {
          throw new Error('Invalid function expression');
        }
      };

      // Generate values approaching the point from both sides
      const values = [];
      const steps = 10;
      const epsilon = 1e-6;

      // Calculate left-hand limit
      let leftValues = [];
      if (direction !== 'right') {
        for (let i = 1; i <= steps; i++) {
          const approachValue = pointNum - epsilon * Math.pow(10, steps - i);
          leftValues.push(safeEval(func, variable, approachValue));
        }
        values.push(...leftValues);
      }

      // Calculate right-hand limit
      let rightValues = [];
      if (direction !== 'left') {
        for (let i = 1; i <= steps; i++) {
          const approachValue = pointNum + epsilon * Math.pow(10, steps - i);
          rightValues.push(safeEval(func, variable, approachValue));
        }
        values.push(...rightValues);
      }

      // Determine the limit
      let limit;
      if (direction === 'twoSided') {
        if (Math.abs(leftValues[steps-1] - rightValues[steps-1]) < epsilon) {
          limit = (leftValues[steps-1] + rightValues[steps-1]) / 2;
        } else {
          throw new Error('Left and right limits do not converge');
        }
      } else if (direction === 'left') {
        limit = leftValues[steps-1];
      } else {
        limit = rightValues[steps-1];
      }

      // Create visualization data
      const xValues = [];
      const yValues = [];
      
      if (direction !== 'right') {
        for (let i = 1; i <= steps; i++) {
          const approachValue = pointNum - epsilon * Math.pow(10, steps - i);
          xValues.push(approachValue);
          yValues.push(leftValues[i-1]);
        }
      }
      
      if (direction !== 'left') {
        for (let i = 1; i <= steps; i++) {
          const approachValue = pointNum + epsilon * Math.pow(10, steps - i);
          xValues.push(approachValue);
          yValues.push(rightValues[i-1]);
        }
      }

      const chartData = {
        labels: xValues.map((_, i) => `Point ${i + 1}`),
        datasets: [{
          label: 'Function Values',
          data: yValues,
          fill: false,
          tension: 0.1,
          borderColor: 'rgba(75, 192, 192, 1)',
          backgroundColor: 'rgba(75, 192, 192, 0.5)',
        }]
      };

      // Create calculation steps
      const calculationSteps = [
        `Step 1: Analyze the function: ${func}`,
        `Step 2: Approach the point ${pointNum} from ${direction === 'twoSided' ? 'both sides' : direction === 'left' ? 'the left' : 'the right'}`,
        `Step 3: Evaluate function values approaching the point`,
        `Step 4: Determine the limit based on approaching values`,
        `Step 5: Calculated limit: ${limit.toFixed(4)}`
      ];

      setResult({
        function: func,
        variable,
        point: pointNum,
        direction,
        limit: limit.toFixed(6),
        calculationSteps,
        chartData
      });

    } catch (err) {
      setError(err.message);
    }
  };

  const exampleData = {
    function: 'x**2 - 5',
    variable: 'x',
    point: 3,
    direction: 'twoSided',
    limit: 4
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5 }}
      className="min-h-screen bg-gradient-to-r from-orange-100 to-yellow-100"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Helmet>
          <title>Limit Calculator | Calculate Function Limits</title>
          <meta name="description" content="Calculate limits of mathematical functions as variables approach specific points. This calculator evaluates one-sided and two-sided limits for various functions." />
          <meta name="keywords" content="limit calculator, calculus calculator, function limit, one-sided limit, two-sided limit, math calculator" />
        </Helmet>

        <div className="relative overflow-hidden bg-white rounded-lg shadow-2xl p-8 mb-8">
          <div className="calculator-header flex items-center mb-8">
            <div className="header-icon text-4xl text-orange-600 mr-4">🔬</div>
            <h1 className="text-3xl font-bold text-gray-800">Limit Calculator</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>Calculate limits of mathematical functions as variables approach specific points. This calculator evaluates one-sided and two-sided limits for various functions.</p>
          </div>

          <div className="grid grid-cols-1 gap-6 mb-6">
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Function:</label>
              <input
                type="text"
                name="function"
                value={inputData.function}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder="Enter function (e.g., x**2 - 5)"
              />
            </div>

            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Variable:</label>
              <input
                type="text"
                name="variable"
                value={inputData.variable}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder="Enter variable (default: x)"
              />
            </div>

            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Point:</label>
              <input
                type="number"
                name="point"
                value={inputData.point}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder="Enter point to approach"
              />
            </div>

            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Direction:</label>
              <select
                name="direction"
                value={inputData.direction}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              >
                <option value="twoSided">Two-Sided Limit</option>
                <option value="left">Left-Sided Limit</option>
                <option value="right">Right-Sided Limit</option>
              </select>
            </div>
          </div>

          <button
            onClick={calculateLimit}
            className="w-full py-3 px-4 bg-orange-600 text-white font-medium rounded-lg hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-colors duration-200"
          >
            Calculate Limit
          </button>

          {error && (
            <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
              {error}
            </div>
          )}

          {result && (
            <div className="mt-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Results</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Limit Calculation</h3>
                  <p className="text-lg">Function: {result.function}</p>
                  <p className="text-lg">Variable: {result.variable}</p>
                  <p className="text-lg">Point: {result.point}</p>
                  <p className="text-lg">Direction: {result.direction === 'twoSided' ? 'Two-Sided' : result.direction === 'left' ? 'Left-Sided' : 'Right-Sided'}</p>
                  <p className="text-2xl font-bold text-orange-600 mt-4">Limit: {result.limit}</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Function Visualization</h3>
                  <div className="h-64 bg-white rounded-lg">
                    <Line
                      data={result.chartData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                          y: {
                            title: {
                              display: true,
                              text: 'Function Value'
                            }
                          },
                          x: {
                            title: {
                              display: true,
                              text: 'Approach Value'
                            }
                          }
                        },
                        plugins: {
                          title: {
                            display: true,
                            text: 'Function Behavior Near Limit Point'
                          }
                        }
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-700 mb-4">Calculation Steps</h3>
                <div className="space-y-4">
                  {result.calculationSteps.map((step, index) => (
                    <div key={index} className="p-4 bg-white rounded-lg shadow-inner">
                      <pre className="whitespace-pre-wrap font-mono text-gray-700">{step}</pre>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Example Calculation Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Example Calculation <span className="text-orange-600">📝</span></h2>
          <div className="text-gray-600">
            <p>Here's an example calculation for the limit of x² - 5 as x approaches 3:</p>
            
            <div className="bg-white p-6 rounded-lg shadow-md mt-4">
              <h4 className="mb-4">Sample Data:</h4>
              <ul className="list-disc pl-6 mb-4">
                <li>Function: {exampleData.function}</li>
                <li>Variable: {exampleData.variable}</li>
                <li>Point: {exampleData.point}</li>
                <li>Direction: Two-Sided</li>
                <li>Limit: {exampleData.limit}</li>
              </ul>

              <h4 className="mb-4">Calculation Method:</h4>
              <ol className="list-decimal pl-6 mb-4">
                <li>Approach the point 3 from both sides</li>
                <li>Evaluate function values near 3</li>
                <li>Calculate left-hand and right-hand limits</li>
                <li>Compare results to determine the two-sided limit</li>
              </ol>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use <span className="text-orange-600">📋</span></h2>
          <div className="text-gray-600">
            <p>Follow these steps to use the Limit Calculator:</p>
            <ol className="list-decimal pl-6 mt-4">
              <li><strong>Enter Function:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Input the mathematical function (use JavaScript syntax)</li>
                  <li>Example: x**2 - 5, Math.sin(x), Math.exp(x)</li>
                </ul>
              </li>
              <li><strong>Set Variable:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Specify the variable approaching the limit point</li>
                </ul>
              </li>
              <li><strong>Enter Point:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Input the point the variable is approaching</li>
                </ul>
              </li>
              <li><strong>Select Direction:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Choose between two-sided, left-sided, or right-sided limit</li>
                </ul>
              </li>
              <li><strong>Click Calculate:</strong> The calculator will determine:
                <ul className="list-disc pl-6 mt-2">
                  <li>The limit value</li>
                  <li>A visualization of the function near the limit point</li>
                  <li>Detailed calculation steps</li>
                </ul>
              </li>
            </ol>
            <div className="mt-4 p-4 bg-orange-100 rounded-lg">
              <p><strong>💡 Pro Tips:</strong></p>
              <ul className="list-disc pl-6 mt-2">
                <li>Use Math. for mathematical functions (e.g., Math.sin(x), Math.exp(x))</li>
                <li>For powers, use ** operator (e.g., x**2 for x squared)</li>
                <li>Complex functions may require more precise evaluation</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Understanding Limits <span className="text-orange-600">💻</span></h2>
          <div className="text-gray-600">
            <p>
              The limit of a function describes the behavior of the function as its input approaches a certain value. Limits are fundamental in calculus and mathematical analysis:
            </p>
            <ul className="list-disc pl-6 mt-4">
              <li><strong>Two-Sided Limit:</strong> The value the function approaches as the input approaches a point from both sides</li>
              <li><strong>Left-Sided Limit:</strong> The value the function approaches as the input approaches a point from values less than the point</li>
              <li><strong>Right-Sided Limit:</strong> The value the function approaches as the input approaches a point from values greater than the point</li>
            </ul>
            <p className="mt-4">
              Limits are used in:
              <ul className="list-disc pl-6 mt-4">
                <li><strong>Calculus:</strong> Defining derivatives and integrals</li>
                <li><strong>Physics:</strong> Analyzing motion and change</li>
                <li><strong>Engineering:</strong> System stability analysis</li>
                <li><strong>Economics:</strong> Marginal analysis and optimization</li>
              </ul>
            </p>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Best Practices <span className="text-orange-600">✨</span></h2>
          <div className="text-gray-600">
            <ul className="list-disc pl-6 mt-4">
              <li><strong>Understand Function Behavior:</strong> Analyze how the function behaves near the point</li>
              <li><strong>Check Both Sides:</strong> For two-sided limits, verify left and right limits agree</li>
              <li><strong>Use Multiple Approaches:</strong> Approach the point from different directions to confirm results</li>
              <li><strong>Be Mindful of Discontinuities:</strong> Functions with discontinuities may have different left and right limits</li>
            </ul>
            <p className="italic mt-4">
              Note: For functions with essential singularities or complex behavior, numerical methods may not capture all limit properties.
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default LimitCalculator;